/**
 * Data. Section 6.9: observed facts only.
 *
 * "Wording rule: never display 'Saved' unless a write has returned successfully. If the
 * state is unknown, say 'unknown'."
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { Banner } from './components.tsx';
import { store } from '../data/client.ts';
import type { Stats, SelfTestResult } from '../data/client.ts';
import { installState } from '../data/installState.ts';
import { META } from '../plan/data.ts';
import { positionFor } from '../plan/dates.ts';
import { isDeload } from '../plan/phase.ts';

const fmtTime = (ms: number | null | undefined) =>
  ms === null || ms === undefined ? 'unknown'
    : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'medium' });
const fmtBytes = (b: number | null | undefined) =>
  b === null || b === undefined ? 'unknown'
    : b < 1024 ? `${b} B` : b < 1024 ** 2 ? `${(b / 1024).toFixed(1)} kB`
    : `${(b / 1024 ** 2).toFixed(2)} MB`;

export function Data({ api }: { api: StoreApi }) {
  const { durable, capabilities: caps } = api;
  const [stats, setStats] = useState<Stats | null>(null);
  const [integrity, setIntegrity] = useState('unknown');
  const [prag, setPrag] = useState<Record<string, string> | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [install] = useState(installState);
  const [tests, setTests] = useState<SelfTestResult[] | null>(null);
  const [testing, setTesting] = useState(false);
  const [stage, setStage] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const startDate = api.state.get<string>('setting:startDate') ?? META.defaultStartDate;
  const pos = positionFor(startDate, new Date());
  const checkpointWeek = isDeload(pos.week);

  const refresh = useCallback(async () => {
    try {
      setStats(await store.stats());
      setIntegrity(await store.integrityCheck());
      setPrag(await store.pragmas());
      await api.refreshMirror();
    } catch (e) { setNote(e instanceof Error ? e.message : String(e)); }
  }, [api]);

  useEffect(() => { if (durable) void refresh(); }, [durable, refresh]);

  const doExport = async () => {
    setBusy(true); setNote(null);
    try {
      const doc = await store.exportJson();
      const text = JSON.stringify(doc, null, 2);
      const name = `fl73-export-${new Date().toISOString().slice(0, 10)}.json`;
      const file = new File([new Blob([text], { type: 'application/json' })], name,
        { type: 'application/json' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: 'FL73 export' });
        setNote(`Shared ${doc.rows.length} rows.`);
      } else {
        await navigator.clipboard.writeText(text);
        setNote(`Share sheet unavailable — ${doc.rows.length} rows copied to the clipboard.`);
      }
    } catch (e) { setNote(`Export failed: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusy(false); }
  };

  const doImport = async (mode: 'merge' | 'replace', text: string) => {
    setBusy(true); setNote(null);
    try {
      const doc = JSON.parse(text);
      const res = mode === 'merge' ? await store.importMerge(doc) : await store.importReplace(doc);
      setNote(`Import (${mode}): ${JSON.stringify(res)}. Reload to see the restored state.`);
      await refresh();
    } catch (e) { setNote(`Import failed: ${e instanceof Error ? e.message : String(e)}`); }
    finally { setBusy(false); }
  };

  return (
    <>
      <h1>Data</h1>
      <p className="sub">Observed facts only. Nothing here reports intent.</p>

      {!durable && (
        <Banner kind="bad" title="Storage is not working. Nothing is being saved.">
          {api.openError ?? caps?.reason ?? 'The database did not open, and no reason was reported.'}
        </Banner>
      )}
      {durable && !install.standalone && (
        <Banner kind="warn" title="Running in a browser tab, not as an installed app">
          Safari can evict storage for a site you have not opened in seven days. Add this to
          your Home Screen and open it from the icon.
        </Banner>
      )}
      {checkpointWeek && (
        <Banner kind="info" title={`Week ${pos.week} is a checkpoint week`}>
          Worth exporting now. Sixteen weeks of data is worth one dialogue box a month.
        </Banner>
      )}

      <h2>Local database</h2>
      <div className="card">
        <Row k="Status" v={durable ? <span className="ok">open</span> : <span className="bad">not open</span>} />
        <Row k="Engine" v={`SQLite ${caps?.sqliteVersion ?? 'unknown'}`} />
        <Row k="Storage" v={caps?.vfs ?? 'none'} />
        <Row k="Installed (standalone)" v={install.standalone
          ? <span className="ok">yes — {install.source}</span>
          : <span className="bad">no — {install.source}</span>} />
        <Row k="Persistent storage granted" v={
          caps === null || caps.persisted === null ? 'unknown'
            : install.isIos ? <span className="dimmed">{String(caps.persisted)} (not meaningful on iOS)</span>
            : caps.persisted ? <span className="ok">yes</span> : <span className="bad">no</span>} />
        <Row k="Journal mode" v={prag?.journal_mode ?? 'unknown'} />
        <Row k="Synchronous" v={prag?.synchronous ?? 'unknown'} />
        <Row k="Integrity check" v={<span className={integrity === 'ok' ? 'ok' : 'bad'}>{integrity}</span>} />
        <Row k="Rows (live / tombstone)" v={stats ? `${stats.liveRowCount} / ${stats.tombstoneCount}` : 'unknown'} />
        <Row k="Last successful write" v={fmtTime(stats?.lastWriteAt)} />
        <Row k="Database size" v={fmtBytes(stats?.pageBytes)} />
        <Row k="Storage used / quota" v={caps ? `${fmtBytes(caps.usageBytes)} / ${fmtBytes(caps.quotaBytes)}` : 'unknown'} />
        <Row k="Device id" v={<span className="mono">{store.deviceId}</span>} />
        <div className="btns"><button onClick={() => void refresh()} disabled={!durable}>Refresh</button></div>
      </div>

      <h2>Sheet mirror</h2>
      <div className="card">
        <Row k="Last successful push" v={api.mirror?.lastSuccessAt
          ? <span className="ok">{fmtTime(api.mirror.lastSuccessAt)}</span>
          : <span className="warn">never</span>} />
        <Row k="Rows waiting" v={api.queued === null ? 'unknown' : String(api.queued)} />
        <Row k="Rows in last push" v={api.mirror?.lastRowsPushed == null ? 'none yet'
          : String(api.mirror.lastRowsPushed)} />
        <Row k="Last attempt" v={fmtTime(api.mirror?.lastAttemptAt)} />
        <Row k="Consecutive failures" v={api.mirror ? String(api.mirror.consecutiveFailures) : 'unknown'} />
        {api.mirror?.lastError && (
          <Banner kind="bad" title="Last error, verbatim">
            <span className="mono">{api.mirror.lastError}</span>
          </Banner>
        )}
        <div className="btns">
          <button disabled={!durable || busy} onClick={() => void api.pushNow()}>Push now</button>
          <button disabled={!durable || busy} onClick={async () => {
            if (!confirm('Queue every local row for re-upload? Use this if the sheet has been '
              + 'cleared or looks wrong. It rebuilds the sheet from this device and changes '
              + 'nothing locally.')) return;
            setBusy(true);
            try { await store.markAllUnsynced(); await api.pushNow(); }
            finally { setBusy(false); }
          }}>Rebuild sheet from this device</button>
        </div>
        <p className="sub" style={{ marginBottom: 0 }}>
          One way: this device to the sheet. Nothing in the sheet is ever read back, and a
          failed push never touches local state.
        </p>
      </div>

      <h2>Storage self-test</h2>
      <div className="card">
        <p className="sub" style={{ marginTop: 0 }}>
          Runs the persistence assertions against real SQLite on this device, in scratch
          databases. It leaves your data alone. It cannot prove survival of a force-quit —
          only force-quitting can, so do that too.
        </p>
        <div className="btns">
          <button disabled={!durable || testing} onClick={async () => {
            setTesting(true); setTests(null); setStage(null);
            store.onProgress = (s) => setStage(s);
            try { setTests(await store.selfTest()); }
            catch (e) { setNote(`Self-test could not run: ${e instanceof Error ? e.message : String(e)}`); }
            finally { setTesting(false); store.onProgress = null; void refresh(); }
          }}>{testing ? 'Running…' : 'Run self-test'}</button>
        </div>
        {testing && stage && <div className="sub" style={{ marginTop: 8 }}>Running: {stage}</div>}
        {tests && (
          <>
            <div className="row" style={{ marginTop: 10 }}>
              <span className="k">Result</span>
              <span className={`v ${tests.every((t) => t.passed) ? 'ok' : 'bad'}`}>
                {tests.filter((t) => t.passed).length} of {tests.length} passed
              </span>
            </div>
            {tests.map((t) => (
              <div className="test" key={t.name}>
                <span className={`mark ${t.passed ? 'ok' : 'bad'}`}>{t.passed ? '✓' : '✕'}</span>
                <span><div>{t.name}</div><div className="detail">{t.detail}</div></span>
              </div>
            ))}
          </>
        )}
      </div>

      <h2>Backup</h2>
      <div className="card">
        <p className="sub" style={{ marginTop: 0 }}>
          The only mechanism you can verify yourself. Export includes timestamps and
          tombstones, so a restore is exact.
        </p>
        <div className="btns">
          <button className="primary" disabled={!durable || busy} onClick={() => void doExport()}>Export</button>
          <button disabled={!durable || busy} onClick={() => fileRef.current?.click()}>Import (merge)</button>
          <button className="danger" disabled={!durable || busy} onClick={async () => {
            if (!confirm('REPLACE all local data with an imported file? Everything currently '
              + 'on this device is wiped first.')) return;
            const text = prompt('Paste the export JSON');
            if (text) await doImport('replace', text);
          }}>Import (replace)</button>
        </div>
        <input ref={fileRef} type="file" accept="application/json,.json" style={{ display: 'none' }}
               onChange={async (e) => {
                 const f = e.target.files?.[0]; if (!f) return;
                 await doImport('merge', await f.text()); e.target.value = '';
               }} />
        <p className="sub" style={{ marginBottom: 0 }}>
          <strong>Merge</strong> applies per-key last-write-wins, which is the safe default.
          <strong> Replace</strong> wipes and restores verbatim.
        </p>
      </div>

      {note && <Banner kind={/failed/i.test(note) ? 'bad' : 'ok'}>{note}</Banner>}
    </>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="row"><span className="k">{k}</span><span className="v">{v}</span></div>;
}
